import {Injectable} from '@angular/core';
import {catchError, map, Observable, of, tap} from 'rxjs';
import {SpatialSnapshotStore} from './spatial-snapshot-store.service';
import {switchMap} from 'rxjs/operators';
import {SpatialUtilService} from '../services/spatial-util.service';
import {NotificationAlertsService} from '../../common/notification-alerts/notification-alerts.service';
import {PlanModelService} from '../../common/service/plan-model.service';
import {SpatialDiffService} from '../services/spatial-diff.service';
import {SpatialSnapshotsModel} from './spatial-snapshot.model';
import {forkJoinDefaultIfEmpty} from '../../common/observable.utils';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable  @typescript-eslint/no-unused-vars */
@Injectable({
    providedIn: 'root'
})
export class SpatialSnapshotService {
    title = 'Spatial Snapshot Service';
    maxSnapShotCount = 50;

    planGuid: string;

    getLayers: () => any[];

    getSpatialReference: () => any;

    constructor(
        private planModelService: PlanModelService,
        private notificationAlertsService: NotificationAlertsService,
        private spatialDiffService: SpatialDiffService,
        private spatialSnapshotStore: SpatialSnapshotStore,
        private spatialUtilService: SpatialUtilService
    ) {}

    getAllSpatialFeatures() {
        console.debug(`${this.title} - getAllSpatialFeatures()`);
        return this.getLayers().map(v => ({
            layer: v.featureLayer.id,
            features: this.spatialUtilService.getFeatures(v.featureLayer.id).map(f => (f.toJson ? f.toJson() : f))
        }));
    }

    createSnapshot(options: any = {}) {
        console.debug(`${this.title} - createSnapshot()`);

        const res = this.getAllSpatialFeatures();

        return this.spatialSnapshotStore.getSnapshotCount(this.planGuid).pipe(
            switchMap(count => {
                if (count < this.maxSnapShotCount) {
                    const snapshotsArray = [];
                    snapshotsArray.push({datetime: this.toLocalIsoString(), layers: res});
                    const observable = this.spatialSnapshotStore.createSnapshot({id: this.planModelService.getGuid(), snapshots: snapshotsArray});
                    if (options.showAlert) {
                        return observable.pipe(tap(() => this.notificationAlertsService.showSnapshotSaveSuccess()));
                    }
                    return observable;
                } else {
                    // remove the oldest snapshot and create a new one
                    return this.clearSnapshot(this.spatialSnapshotStore.snapshotIds[count - 1]).pipe(
                        switchMap(() =>
                            this.spatialSnapshotStore.createSnapshot({
                                id: this.planModelService.getGuid(),
                                snapshots: [{datetime: this.toLocalIsoString(), layers: res}]
                            })
                        )
                    );
                }
            })
        );
    }

    setupSnapshot(): Observable<any> {
        console.debug(`${this.title} - setupSnapshot()`);
        return this.spatialSnapshotStore.getSnapshotCount(this.planGuid).pipe(
            switchMap(count => {
                if (count === 0 && count < this.maxSnapShotCount) {
                    return this.createSnapshot().pipe(switchMap(() => this.spatialSnapshotStore.getAllPlanSnapshots(this.planGuid)));
                }
                return this.spatialSnapshotStore.getAllPlanSnapshots(this.planGuid);
            })
        );
    }

    featuresMatchSnapshot(id: string) {
        console.debug(`${this.title} - featuresMatchSnapshot(${id})`);
        let allSpatialFeatures = this.getAllSpatialFeatures();
        return this.spatialSnapshotStore.getAllPlanSnapshots(this.planGuid).pipe(
            switchMap(planSnapshots => {
                const snapshot = planSnapshots.snapshots.map(f => ({datetime: f.datetime, layers: f.layers})).find(f => f.datetime === id);

                allSpatialFeatures = allSpatialFeatures.filter(v => {
                    return !['interestFeatureLayer', 'roadFeatureLayer'].includes(v.layer);
                });

                return forkJoinDefaultIfEmpty(
                    snapshot.layers
                        .filter(v => !['interestFeatureLayer', 'roadFeatureLayer'].includes(v.layer))
                        .map((v, i: number) => this.spatialDiffService.getLayerDiff(v.layer, v.features, allSpatialFeatures[i].features))
                ).pipe(
                    map((layerDiffs: any[]) => {
                        this.cleanMemory(snapshot, allSpatialFeatures);
                        return !layerDiffs.some(function (v) {
                            return v.adds.length || v.updates.length || v.deletes.length;
                        });
                    })
                );
            })
        );
    }

    cleanMemory(snapshot: any, allSpatialFeatures: any) {
        console.debug(`${this.title} - cleanMemory()`);

        snapshot.layers.forEach((layer: any) => delete layer.features);

        allSpatialFeatures.forEach((layer: any) => delete layer.features);
    }

    featuresMatchBaseSnapshot() {
        console.debug(`${this.title} - featuresMatchBaseSnapshot()`);
        return this.featuresMatchSnapshot(this.spatialSnapshotStore.baseSnapshotId);
    }

    restoreSnapshot(id: string) {
        console.debug(`${this.title} - restoreSnapshot(${id})`);

        const allSpatialFeatures = this.getAllSpatialFeatures();

        return this.spatialSnapshotStore.getAllPlanSnapshots(this.planGuid).pipe(
            switchMap((response: SpatialSnapshotsModel) => {
                const snapshot = response.snapshots.map(f => ({datetime: f.datetime, layers: f.layers})).find(f => f.datetime === id);

                return forkJoinDefaultIfEmpty(
                    snapshot.layers.map((v, i: number) => this.spatialDiffService.getLayerDiff(v.layer, v.features, allSpatialFeatures[i].features))
                ).pipe(
                    switchMap((layerDiffs: any[]) => {
                        return forkJoinDefaultIfEmpty(
                            layerDiffs.map((v, i: number) => {
                                const layer = this.getLayers().find(v => v.featureLayer.id === snapshot.layers[i].layer);
                                const featureLayer = layer.featureLayer;

                                if (v.adds.length > 0 || v.updates.length > 0 || v.deletes.length > 0) {
                                    return this.spatialUtilService.applyEdits(featureLayer, v.adds, v.updates, v.deletes);
                                }
                                return of(null);
                            })
                        );
                    })
                );
            }),
            tap(() => {
                console.debug(`${this.title} - restoreSnapshot - success`);
                this.spatialSnapshotStore.baseSnapshotId = id;
            })
        );
    }

    clearSnapshot(id: string) {
        console.debug(`${this.title} - clearSnapshot(${id})`);
        return this.spatialSnapshotStore.clearSnapshot(this.planGuid, id);
    }

    clearAllSnapshots() {
        console.debug(`${this.title} - clearAllSnapshots()`);
        return this.spatialSnapshotStore.clearAllSnapshots();
    }

    getSnapshotIds() {
        return this.spatialSnapshotStore.snapshotIds;
    }

    init(getPlanGuid: () => string, getSpatialReference: () => any, getLayers: () => any[]) {
        console.debug(`${this.title} - init`);

        this.planGuid = getPlanGuid();

        this.getSpatialReference = getSpatialReference;

        this.getLayers = getLayers;

        return this.spatialSnapshotStore.setupDb(this.planGuid).pipe(
            tap(() => console.debug(`${this.title} - init - success`)),
            catchError(reason => {
                console.debug(`${this.title} - init - error:`, reason);
                return reason;
            })
        );
    }

    private pad(value: number, size: number) {
        return `00${value}`.slice(-size);
    }

    private toLocalIsoString() {
        const d = new Date();
        return (
            `${this.pad(d.getDate(), 2)}-${this.pad(d.getMonth() + 1, 2)}-${d.getFullYear()}` +
            ` ${this.pad(d.getHours(), 2)}:${this.pad(d.getMinutes(), 2)}:${this.pad(d.getSeconds(), 2)}.${this.pad(d.getMilliseconds(), 3)}`
        );
    }
}
